/**
 * Shared helpers for the desktop tool-launch surfaces
 * (DesktopHome, CategoryLauncher).
 *
 * Keeps the three repeated patterns DRY:
 *   - buildAllTools()       — flat array of all tools with category metadata
 *   - openPdfForMode()      — dispatch a file-open result to the viewer
 *   - resolveToolClick()    — Pro-lock / action / path routing for a tool card
 */
import { CATEGORIES } from '@/lib/toolCategories';
import { isProFeature, hasFeature } from '@/lib/license';

/**
 * Flat list of all tools, each stamped with their category id and colors.
 * Mirrors the ALL_TOOLS build in DesktopHome and Dashboard.
 */
export function buildAllTools() {
  return CATEGORIES.flatMap((category) =>
    category.tools.map((tool) => ({
      ...tool,
      tabId: tool.displayTab || category.id,
      colors: tool.displayColors || category.colors,
    }))
  );
}

/**
 * Given a file-open result (from useFileDialog) and a mode string,
 * navigate to the correct viewer route.
 *
 * @param {object} r        — result from openFile() { buffer, name, path, cloudDocId }
 * @param {string} mode     — 'editText' | 'annotate' | 'sign' | undefined
 * @param {Function} navigate — React Router navigate()
 */
export function openPdfForMode(r, mode, navigate) {
  if (!r) return;
  const file = new File([r.buffer], r.name, { type: 'application/pdf' });
  const dest =
    mode === 'annotate'
      ? '/viewer?mode=edit'
      : mode === 'editText'
      ? '/viewer?mode=editText'
      : mode === 'sign' ? '/viewer?mode=sign' : '/viewer';
  navigate(dest, { state: { newDocument: true, file, filePath: r.path, cloudDocId: r.cloudDocId || null } });
}

/**
 * Decide what happens when a tool card is clicked.
 *
 * @param {object} tool       — tool entry from CATEGORIES / buildAllTools()
 * @param {object} deps
 * @param {Function} deps.onOpenFile  — async (mode?) => void  (calls openFile + openPdfForMode)
 * @param {Function} deps.navigate    — React Router navigate()
 */
export function resolveToolClick(tool, { onOpenFile, navigate }) {
  const { path, action, feature, pro } = tool;
  const isPro = pro || (feature && isProFeature(feature));
  const locked = isPro && !hasFeature(feature);

  if (locked) {
    navigate(path || '/account/billing');
    return;
  }
  if (action === 'openFile') { onOpenFile(); return; }
  if (action === 'openFileEditText') { onOpenFile('editText'); return; }
  if (action === 'openFileAnnotate') { onOpenFile('annotate'); return; }
  if (action === 'openFileSign') { onOpenFile('sign'); return; }
  if (path) { navigate(path); return; }
}
